import {
  Button,
  Checkbox,
  Grid,
  Paper,
  PinInput,
  Select,
  TextInput,
  createTheme,
} from '@mantine/core';
import textInputClasses from '@/assets/styles/mantine/textInput.module.css';
import pinInputClasses from '@/assets/styles/mantine/pinInput.module.css';
import checkboxClasses from '@/assets/styles/mantine/checkbox.module.css';
import selectClasses from '@/assets/styles/mantine/select.module.css';
import timeGridClasses from '@/assets/styles/mantine/timeGrid.module.css';
import { TimeGrid } from '@mantine/dates';
import paperClasses from '@/assets/styles/mantine/paper.module.css';

const mantineTheme = {
  fontFamily: 'Rubik, Arial',
  fontSizes: {
    xs: '0.5rem',
    sm: '0.7rem',
    md: '1rem',
    lg: '1.25rem',
    xl: '1.5rem',
  },
  black: 'black',
  white: '#fff',
  cursorType: 'pointer',
  primaryColor: 'brand',
  colors: {
    brand: [
      '#edebfa',
      '#cdc8f1',
      '#b6afed',
      '#8e83e1',
      '#6c5dd6',
      '#5646d1',
      '#4b3acf',
      '#3c2cb7',
      '#3427a5',
      '#2a2092',
    ],
    secondary: [
      '#d0ebd5',
      '#b4dfbb',
      '#97d2a1',
      '#7bc688',
      '#8692b2', // main
      '#48a758',
      '#3c8b49',
      '#2f6e3a',
      '#23522b',
      '#17351c',
    ],
    gray: [
      '#F8F7FC', // Lightest background, cards
      '#E3E1EC', // Base color (subtle borders)
      '#D0CDDD', // Dividers, hover states
      '#888D9F', // Secondary text, icons
      '#A9A5BF', // Disabled elements
      '#9592AA', // Subtle borders
      '#7B7894', // Primary text (light mode)
      '#615E7A', // Headings, emphasized text
      '#47445F', // Dark backgrounds (dark mode)
      '#2D2A45', // High-contrast text
      '#DCE0E5',
      '#6f7c8e', // table headers
      '#181C29',
      '#fafaf9',
      '#eef0f8',
      '#e7e5e4',
      '#d6d3d1',
      '#a8a29e',
      '#78716c',
      '#57534e',
      '#44403c',
      '#292524',
      '#1c1917',
    ],
  },
  components: {
    TimeGrid: TimeGrid.extend({
      classNames: timeGridClasses,
      defaultProps: {
        withSeconds: false,
        simpleGridProps: {
          type: 'container',
          cols: { base: 1, '180px': 2, '320px': 3 },
          spacing: 'xs',
        },
      },
    }),
    Grid: Grid.extend({ defaultProps: { gutter: 'xl' } }),
    Button: Button.extend({
      defaultProps: {
        size: 'md',
      },
    }),
    PinInput: PinInput.extend({
      classNames: pinInputClasses,
      defaultProps: {
        placeholder: '',
        length: 6,
        type: 'number',
        oneTimeCode: true,
      },
    }),
    TextInput: TextInput.extend({
      classNames: textInputClasses,
      defaultProps: {
        placeholder: '',
        autoComplete: 'off',
        inputWrapperOrder: ['label', 'input', 'error', 'description'],
      },
    }),
    Checkbox: Checkbox.extend({
      classNames: checkboxClasses,
      defaultProps: {
        size: 'md',
      },
    }),
    Select: Select.extend({
      classNames: selectClasses,
    }),
    Paper: Paper.extend({
      classNames: paperClasses,
      defaultProps: {
        withBorder: true,
        px: '3rem',
        radius: 'md',
        py: 'md',
        mb: 'lg',
      },
    }),
  },
};

const resolver = () => ({
  variables: {
    '--f-header-height': '60px',
    '--f-page-width': '70rem',
    '--f-z-header': '2',
    '--f-z-side': '3',
    '--f-page-padding-inline': '20px',
    '--f-page-head-height': '100px',
    '--f-aside-width': '70px',
    '--f-aside-width-open': '200px',
    '--f-aside-spacing': '8px',
    '--f-color-danger': '#ff0000',
    '--f-color-link': 'var(--mantine-color-brand-5)',
    '--th-f-page-part-width-xs': '80px',
    '--f-page-part-widsm': '140px',
    '--f-page-part-width-md': '200px',
    '--f-page-part-width-lg': '260px',
    '--f-page-part-width-xl': '320px',
    '--f-color-body': '#F5F6FA',
    '--f-color-text': '#0A3238',
    '--f-color-brand': 'var(--mantine-color-brand-6)',
    '--f-color-secondary': 'var(--mantine-color-secondary-4)',
    '--mantine-color-body': '#F6F7F9',
  },
  light: {
    '--f-color-body': '#F5F6FA',
    '--f-color-side': '#F5F6FA',
    '--f-color-text': '#0A3238',
    '--f-color-brand': 'var(--mantine-color-brand-6)',
    '--f-color-secondary': 'var(--mantine-color-secondary-4)',
    '--f-color-danger': '#ff0000',
    '--f-color-link': 'var(--mantine-color-brand-5)',
  },
  dark: {
    '--f-color-body': '#333333',
    '--f-color-side': '#1c1917',
    '--f-color-text': '#e3e3e3',
    '--f-color-brand': 'var(--mantine-color-brand-6)',
    '--f-color-secondary': 'var(--mantine-color-secondary-4)',
    '--f-color-danger': '#ff0000',
    '--f-color-link': 'var(--mantine-color-brand-5)',
  },
});

const dates = {
  locale: 'he',
  firstDayOfWeek: 0, // sunday
  timezone: 'Asia/Jerusalem',
};

const theme = createTheme(mantineTheme);

export {
  theme,
  resolver,
  dates,
};
